import React, { useState } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import ChevronIcon from "@material-ui/icons/KeyboardArrowDownRounded";
import AddIcon from "@material-ui/icons/Add";
import CheckIcon from "@material-ui/icons/Check";
import ProjectIcon from "@material-ui/icons/LayersOutlined";
import RecordVoiceIcon from "@material-ui/icons/RecordVoiceOverOutlined";
import EditIcon from "@material-ui/icons/EditOutlined";

import { useProject } from "../context/Project/ProjectContext";
import { useConfirm } from "../components/ConfirmDialog";
import api from "../services/api";
import toastError from "../errors/toastError";
import VoicePicker from "../pages/Applications/VoicePicker";

// tokens alinhados ao header/menu (layout/index.js) — mesmo padrão de cor/fonte
const T = { line: "#E6E9ED", ink: "#1B222A", ink2: "#5B6470", ink3: "#929AA4", hover: "#F4F6F8", activeBg: "#EFF8D6", activeAccent: "#86C240" };
const MENU_FONT = 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const useStyles = makeStyles({
  // botão no header — mesmo "chip" do usuário (pílula, hover suave, ícone terciário)
  chip: {
    display: "flex", alignItems: "center", gap: 8, height: 34, padding: "0 8px 0 10px",
    borderRadius: 999, cursor: "pointer", textTransform: "none", fontFamily: MENU_FONT,
    fontSize: 12.5, fontWeight: 600, color: T.ink, transition: "background .12s",
    "&:hover": { background: T.hover },
    "& .MuiButton-startIcon": { marginRight: 0, color: T.ink3, "& svg": { fontSize: 19 } },
    "& .MuiButton-endIcon": { marginLeft: 0, color: T.ink3, "& svg": { fontSize: 18 } },
  },
  chipLabel: { maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  // popup no mesmo padrão do menu de usuário
  paper: {
    minWidth: 236, marginTop: 8, padding: 6, background: "#fff",
    border: `1px solid ${T.line}`, borderRadius: 12, boxShadow: "0 14px 38px rgba(27,34,42,.16)", color: T.ink,
  },
  sectionLabel: { padding: "6px 10px 4px", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.ink3 },
  item: {
    fontFamily: MENU_FONT, fontSize: 12.5, fontWeight: 500, color: T.ink, borderRadius: 8, padding: "8px 10px", gap: 11, marginBottom: 1,
    "& svg": { fontSize: 18, color: T.ink3 },
    "&:hover": { background: T.hover },
  },
  itemActive: { background: T.activeBg, fontWeight: 600, "&:hover": { background: T.activeBg }, "& $check": { color: T.activeAccent } },
  itemName: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  check: { color: T.activeAccent, fontSize: 18 },
  divider: { height: 1, background: T.line, margin: "5px 2px" },
});

// header project switcher — everything in the app is scoped to the active project
const ProjectSwitcher = () => {
  const classes = useStyles();
  const { projects, activeProjectId, setActiveProjectId, reloadProjects } = useProject();
  const [anchor, setAnchor] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [makeDefault, setMakeDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceVal, setVoiceVal] = useState({});
  const { prompt } = useConfirm();
  const active = projects.find(p => p.id === activeProjectId);

  const renameProject = async () => {
    setAnchor(null);
    if (!active) return;
    const novo = await prompt({
      title: "Renomear projeto",
      label: "Nome",
      defaultValue: active.name,
      confirmText: "Renomear",
    });
    if (!novo || !novo.trim() || novo.trim() === active.name) return;
    try {
      await api.put(`/projects/${active.id}`, { name: novo.trim() });
      await reloadProjects();
    } catch (e) {
      toastError(e);
    }
  };

  const openVoice = () => {
    setAnchor(null);
    setVoiceVal({
      vendor: active && active.ttsVendor ? active.ttsVendor : "",
      voice: active && active.ttsVoice ? active.ttsVoice : "",
      rate: active && active.ttsRate ? active.ttsRate : "normal",
    });
    setVoiceOpen(true);
  };
  const saveVoice = async () => {
    if (!active || saving) return;
    setSaving(true);
    try {
      await api.put(`/projects/${active.id}`, {
        ttsVendor: voiceVal.vendor || null,
        ttsVoice: voiceVal.voice || null,
        ttsRate: voiceVal.rate || null,
      });
      await reloadProjects();
      setVoiceOpen(false);
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  const openDialog = () => {
    setAnchor(null);
    setName("");
    setMakeDefault(false);
    setDialogOpen(true);
  };

  const createProject = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/projects", {
        name: name.trim(),
        isDefault: makeDefault,
      });
      await reloadProjects();
      if (data && data.id) setActiveProjectId(data.id);
      setDialogOpen(false);
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        className={classes.chip}
        disableRipple
        startIcon={<ProjectIcon />}
        endIcon={<ChevronIcon />}
        onClick={e => setAnchor(e.currentTarget)}
      >
        <span className={classes.chipLabel}>{active ? active.name : "Selecionar projeto"}</span>
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: classes.paper }}
        MenuListProps={{ disablePadding: true }}
      >
        <div className={classes.sectionLabel}>Projetos</div>
        {projects.map(p => (
          <MenuItem
            key={p.id}
            className={clsx(classes.item, p.id === activeProjectId && classes.itemActive)}
            onClick={() => {
              setActiveProjectId(p.id);
              setAnchor(null);
            }}
          >
            <ProjectIcon />
            <span className={classes.itemName}>{p.name}</span>
            {p.id === activeProjectId && <CheckIcon className={classes.check} />}
          </MenuItem>
        ))}
        <div className={classes.divider} />
        {active && (
          <MenuItem className={classes.item} onClick={renameProject}>
            <EditIcon />
            Renomear projeto
          </MenuItem>
        )}
        {active && (
          <MenuItem className={classes.item} onClick={openVoice}>
            <RecordVoiceIcon />
            Voz padrão do projeto
          </MenuItem>
        )}
        <MenuItem className={classes.item} onClick={openDialog}>
          <AddIcon />
          Novo projeto
        </MenuItem>
      </Menu>

      <Dialog open={voiceOpen} onClose={() => !saving && setVoiceOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Voz padrão · {active ? active.name : ""}</DialogTitle>
        <DialogContent>
          <p style={{ fontSize: 13, color: "#5B6470", margin: "0 0 14px" }}>
            Esta voz é usada por padrão nos nós <b>Falar</b> e <b>Menu (URA)</b> deste projeto, a menos
            que o nó defina uma voz própria.
          </p>
          <VoicePicker
            value={voiceVal}
            onChange={setVoiceVal}
            previewText="Olá! Esta é a voz padrão do seu projeto."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoiceOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={saveVoice} variant="contained" color="primary" disabled={saving}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Novo projeto</DialogTitle>
        <form
          onSubmit={e => {
            e.preventDefault();
            createProject();
          }}
        >
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              label="Nome do projeto"
              value={name}
              onChange={e => setName(e.target.value)}
            />
            <FormControlLabel
              style={{ marginTop: 8 }}
              control={
                <Checkbox
                  color="primary"
                  checked={makeDefault}
                  onChange={e => setMakeDefault(e.target.checked)}
                />
              }
              label="Tornar projeto padrão"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={!name.trim() || saving}
            >
              Criar
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default ProjectSwitcher;
